<template>
  <div class="datepicker">
    <div class="header">
      <span class="day-display" v-if="selectedDate">{{
        selectedDayDisplay
      }}</span>

      <div class="month-year">
        <CustomFilterSelectMonths
          v-model="pickupMonth"
          :options="monthNames"
          :viewedMonthName="viewedMonthName"
          :currentMonth="currentMonth"
          :currentYear="currentYear"
        />
        <CustomFilterSelectYears
          v-model="pickupYear"
          :options="availableYears"
          :currentYear="currentYear"
        />
      </div>
    </div>

    <hr />

    <div class="day-names">
      <span v-for="day in dayNamesAbbreviated" :key="day">{{ day }}</span>
    </div>

    <div class="days">
      <span
        v-for="date in dates"
        :key="dateKey(date)"
        :class="dateClasses(date)"
        @click="handleDateClick(date)"
      >
        {{ displayDate(date) }}
      </span>
    </div>
  </div>
</template>

<script>
import CustomFilterSelectMonths from "@/components/CustomFilterSelectMonths.vue";
import CustomFilterSelectYears from "@/components/CustomFilterSelectYears.vue";

export default {
  name: "CustomDatePickerContent",
  components: {
    CustomFilterSelectMonths,
    CustomFilterSelectYears
  },
  props: {
    disabledDates: {
      type: Array,
      default: () => []
    },
    previousSelectedDate: {
      type: Date,
      default: null
    },
    minDate: {
      type: Date,
      required: true
    },
    pickupMonth: {
      type: Number,
      default: null
    },
    pickupYear: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      selectedDate: null,
      currentDate: this.previousSelectedDate || new Date(this.minDate),
      dayNamesAbbreviated: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      dayNamesFull: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ],
      monthNames: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ],
      availableYears: Array.from(
        { length: 3 },
        (_, i) => new Date().getFullYear() + i
      ).filter(year => year >= new Date().getFullYear())
    };
  },
  computed: {
    dates() {
      let daysInMonth = new Date(
        this.currentYear,
        this.currentMonth + 1,
        0
      ).getDate();
      let firstDayOfMonth = new Date(
        this.currentYear,
        this.currentMonth,
        1
      ).getDay();
      let dates = [];

      // Days of the preceding month
      let precedingMonthLastDay = new Date(
        this.currentYear,
        this.currentMonth,
        0
      ).getDate();
      for (let i = 0; i < firstDayOfMonth; i++) {
        dates.unshift({
          date: precedingMonthLastDay - i,
          month: this.currentMonth - 1,
          year:
            this.currentMonth === 0 ? this.currentYear - 1 : this.currentYear,
          disabled: true // This line takes care of the requirement for preceding month
        });
      }

      // Actual days of the month
      for (let i = 1; i <= daysInMonth; i++) {
        let currentDateObj = new Date(this.currentYear, this.currentMonth, i);
        dates.push({
          date: i,
          month: this.currentMonth,
          year: this.currentYear,
          disabled: this.isDateDisabled(currentDateObj)
        });
      }

      // Days of the subsequent month
      let counter = 1;
      while (dates.length % 7 !== 0) {
        dates.push({
          date: counter,
          month: this.currentMonth + 1 === 12 ? 0 : this.currentMonth + 1,
          year:
            this.currentMonth + 1 === 12
              ? this.currentYear + 1
              : this.currentYear,
          disabled: true // This line takes care of the requirement for subsequent month
        });
        counter++;
      }

      return dates;
    },
    currentMonth() {
      return this.currentDate.getMonth();
    },
    currentYear() {
      return this.currentDate.getFullYear();
    },
    selectedDayDisplay() {
      return `${
        this.dayNamesFull[this.selectedDate.getDay()]
      } ${this.selectedDate.getDate()}, ${
        this.monthNames[this.selectedDate.getMonth()]
      } ${this.selectedDate.getFullYear()}`;
    },

    currentMonthName() {
      return this.monthNames[this.selectedDate.getMonth()];
    },
    viewedMonthName() {
      return this.monthNames[this.currentMonth];
    },
    defaultSelectedDate() {
      // Check if previousSelectedDate is valid and use it
      if (this.isValidDefaultDate(this.previousSelectedDate)) {
        return new Date(this.previousSelectedDate);
      }
      // Check if pickupMonth is valid and set the month accordingly
      if (this.isValidMonth(this.pickupMonth)) {
        let dateWithPickupMonth = new Date();
        dateWithPickupMonth.setMonth(this.pickupMonth);
        return dateWithPickupMonth;
      }
      // Check if pickupYear is valid and set the year accordingly
      if (this.isValidYear(this.pickupYear)) {
        let dateWithPickupYear = new Date();
        dateWithPickupYear.setFullYear(this.pickupYear);
        return dateWithPickupYear;
      }
      // Default to today's date
      return new Date();
    }
  },
  watch: {
    previousSelectedDate(newVal) {
      if (this.isValidDefaultDate(newVal)) {
        this.currentDate = new Date(newVal);
      } else {
        this.currentDate = new Date(); // default to today's date if not valid
      }
    },
    pickupMonth: {
      immediate: true,
      handler(newMonth) {
        if (newMonth !== null) {
          let monthNumber;
          switch (newMonth) {
            case "January":
              monthNumber = 0;
              break;
            case "February":
              monthNumber = 1;
              break;
            case "March":
              monthNumber = 2;
              break;
            case "April":
              monthNumber = 3;
              break;
            case "May":
              monthNumber = 4;
              break;
            case "June":
              monthNumber = 5;
              break;
            case "July":
              monthNumber = 6;
              break;
            case "August":
              monthNumber = 7;
              break;
            case "September":
              monthNumber = 8;
              break;
            case "October":
              monthNumber = 9;
              break;
            case "November":
              monthNumber = 10;
              break;
            case "December":
              monthNumber = 11;
              break;
            default:
              monthNumber = this.currentMonth;
          }
          this.currentDate = new Date(this.currentYear, monthNumber, 1); // Set the first day of the selected month
        }
      }
    },
    pickupYear: {
      immediate: true,
      handler(newYear) {
        if (newYear !== null) {
          this.currentDate = new Date(newYear, this.currentMonth, 1); // Set the first day of the selected year and month
        }
      }
    }
  },
  methods: {
    previousMonth() {
      let newMonth = this.currentDate.getMonth() - 1;
      let newYear = this.currentDate.getFullYear();

      if (newMonth < 0) {
        newMonth = 11; // December
        newYear -= 1; // Go to the previous year
      }

      this.currentDate = new Date(newYear, newMonth);
    },
    nextMonth() {
      let newMonth = this.currentDate.getMonth() + 1;
      let newYear = this.currentDate.getFullYear();

      if (newMonth > 11) {
        newMonth = 0; // January
        newYear += 1; // Go to the next year
      }

      this.currentDate = new Date(newYear, newMonth);
    },
    updateCurrentDate() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth()
      );
    },
    isSelected(date) {
      return (
        this.selectedDate &&
        this.selectedDate.getDate() === date.date &&
        this.selectedDate.getMonth() === date.month &&
        this.selectedDate.getFullYear() === date.year
      );
    },
    isDateDisabled(date) {
      // Set the limit date to 350 days in the future
      const today = new Date();
      const futureLimitDate = new Date();
      futureLimitDate.setDate(today.getDate() + 750);

      // Disable the date if it's more than 750 days (2years) in the future
      if (date > futureLimitDate) {
        return true;
      }

      // Check if it's today's date. If yes, then it shouldn't be disabled
      if (this.isToday(date)) {
        return false;
      }

      // Check if it's today's date. If yes, then it shouldn't be disabled
      if (this.isToday(date)) {
        return false;
      }

      // Check if date is before minDate
      const strippedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );
      const strippedMinDate = new Date(
        this.minDate.getFullYear(),
        this.minDate.getMonth(),
        this.minDate.getDate()
      );

      if (strippedDate < strippedMinDate) {
        return true;
      }

      // Check against disabled date ranges
      for (let range of this.disabledDates) {
        let start = range.start || new Date(0); // Default to the earliest time if null.
        start = new Date(
          start.getFullYear(),
          start.getMonth(),
          start.getDate()
        );

        let end = range.end || new Date(); // Default to now if null.
        end = new Date(end.getFullYear(), end.getMonth(), end.getDate());

        if (strippedDate >= start && strippedDate <= end) {
          return true;
        }
      }

      return false;
    },
    selectDate(date) {
      this.selectedDate = new Date(date.year, date.month, date.date);
      this.applyDate();
    },
    applyDate() {
      if (this.selectedDate) {
        // Emit the selected date to parent
        this.$emit("updateDate", this.selectedDate);
      } else {
        alert("No date selected!");
      }
    },
    isToday(date) {
      const today = new Date();
      return (
        today.getDate() === date.date &&
        today.getMonth() === date.month &&
        today.getFullYear() === date.year
      );
    },
    isValidDefaultDate(date) {
      if (!date) {
        return new Date() >= this.minDate;
      }
      return date >= this.minDate && !this.isDateDisabled(date);
    },
    isValidDate(date) {
      return date && date >= this.minDate;
    },
    isValidMonth(month) {
      return typeof month === "number" && month >= 0 && month <= 11;
    },
    isValidYear(year) {
      return typeof year === "number" && year >= 1900 && year <= 2100;
    },
    dateKey(date) {
      return date ? `${date.date}-${date.month}-${date.year}` : "placeholder";
    },

    dateClasses(date) {
      let dayOfWeek = new Date(date.year, date.month, date.date).getDay();
      return {
        "disabled-date": date && date.disabled,
        today: this.isToday(date),
        selected: this.isSelected(date),
        weekend: dayOfWeek === 0 || dayOfWeek === 6 // Corrected to check for Sunday (0) and Saturday (6)
      };
    },
    displayDate(date) {
      return date ? (date.date < 10 ? "0" + date.date : date.date) : "";
    },
    handleDateClick(date) {
      if (date && !date.disabled) this.selectDate(date);
    }
  },
  mounted() {
    this.selectedDate = this.currentDate;
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/settings.scss";

.datepicker {
  border: none;
  width: 100%;
  max-width: 100%; // Ensure width doesn't exceed its parent
  background-color: #fff;
  box-sizing: border-box;
  padding: 12px;
  overflow: scroll;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  line-height: 24px;
}

.header {
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex-wrap: wrap;
  //margin-bottom: 16px;
  flex-direction: column;
}

.day-display {
  display: flex;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  padding-bottom: 20px;
}

.month-navigation button {
  background: none;
  font-size: 16px; // Noticed a small typo here, replaced 'pm' with 'px'
  cursor: pointer;
  padding: 0;
  line-height: 1; // Removes additional height
}

.month-navigation .button-chip:last-child {
  margin-right: 0;
}

.chips {
  display: flex;
  gap: 10px;
  align-items: center;
  //margin-bottom: 16px;
}

.chip,
.button-chip {
  padding: 4px 8px;
  border: 2px solid #d1d2d6;
  border-radius: 20px;
  background-color: #ffffff;
  box-sizing: border-box;
  color: #30384a;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.button-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px; // Making sure it's a square
  height: 32px;
  border-radius: 50%; // Perfect circle
  margin-right: 8px;
}

.day-names,
.days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  grid-row-gap: 8px;
  margin: 0;
  margin-bottom: 16px;
  box-sizing: border-box;
}

.days span {
  display: flex; // Ensuring the content remains centered
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.days span,
.day-names span {
  width: 32px;
  height: 32px;
  padding: 8px;
  border: none;
  box-sizing: border-box;
  font-size: 12px;
  text-align: center;
  cursor: pointer;
}

.days span:hover {
  background-color: #f0f0f0;
}

.days span.today {
  background-color: #f9f3eb;
  color: #964e1e;
}

.days span.selected {
  background-color: #ac6411;
  color: #ffffff;
  font-weight: bold;
}

.day-names span {
  border: 1px solid transparent; // Give a transparent border to match the days
}

.disabled-date {
  color: #999;
  pointer-events: none; // Prevent clicking
  cursor: not-allowed;
}

.footer-hr {
  margin: 0px;
}

.datepicker-footer {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end; // Align button to the right
  align-items: center;
  border: none !important;

  button {
    background: none;
    cursor: pointer;
    padding: 0;
    border: none;
    font-family: "Inter", sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
  }
}

.datepicker .datepicker-footer .apply-button-chip {
  display: inline-flex !important;
  align-items: center !important;
  padding: 4px 8px !important;
  border: 2px solid #d1d2d6 !important;
  border-radius: 20px !important;
  background-color: #ffffff !important;
  box-sizing: border-box !important;
  color: #30384a !important;
  margin-left: 8px !important;
}

.apply-button-chip {
  all: unset;
}

.day-names span:nth-child(1),
.day-names span:nth-child(7) {
  font-weight: bold; /* Bold for Saturday and Sunday in day names */
}

.days span {
  &.weekend {
    font-weight: bold; /* Bold for dates that are on weekends */
  }
  // ... other styles
}

@media (min-width: 376px) {
  //adds a scroll bar on the smallest mobile screen iPhone SE 375px
  .datepicker {
    overflow: hidden;
  }
}

.month-year {
  display: flex;
  gap: 20px;
  padding-left: 8%;
  padding-right: 10px;
}
</style>
